/**
 * Date Formatter Utility
 * 
 * Fixes timezone bug where dates stored as YYYY-MM-DD in PostgreSQL
 * display one day behind due to UTC/local timezone conversion.
 * 
 * HOW IT WORKS:
 * - Database stores: "2026-03-15" (DATE type, no timezone)
 * - JavaScript's new Date("2026-03-15") treats it as UTC midnight
 * - Browser converts to local time: UTC midnight → EST 7PM previous day
 * - Solution: Parse manually and force UTC formatting
 */

/**
 * Format a date string (YYYY-MM-DD or ISO timestamp) for display
 * @param {string} dateString - Date in YYYY-MM-DD format or ISO timestamp from database
 * @param {object} options - Intl.DateTimeFormat options (optional)
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, options = {}) => {
  if (!dateString) return '';
  
  // Handle both YYYY-MM-DD dates and full ISO timestamps
  let year, month, day;
  
  if (dateString.includes('T')) {
    // It's a timestamp - extract just the date part
    [year, month, day] = dateString.split('T')[0].split('-').map(Number);
  } else {
    // It's already YYYY-MM-DD
    [year, month, day] = dateString.split('-').map(Number);
  }
  
  // Create date object explicitly in UTC
  const date = new Date(Date.UTC(year, month - 1, day));
  
  // Default formatting options
  const defaultOptions = {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC' // Critical: Force UTC to preserve original date
  };
  
  return date.toLocaleDateString('en-US', { ...defaultOptions, ...options });
};

/**
 * Format date for cards/compact display (e.g., "Mar 15, 2026")
 * @param {string} dateString - Date in YYYY-MM-DD format or ISO timestamp
 * @returns {string} Formatted date string
 */
export const formatDateShort = (dateString) => {
  if (!dateString) return '';
  
  // Handle both YYYY-MM-DD dates and full ISO timestamps
  let year, month, day;
  
  if (dateString.includes('T')) {
    // It's a timestamp - extract just the date part
    [year, month, day] = dateString.split('T')[0].split('-').map(Number);
  } else {
    // It's already YYYY-MM-DD
    [year, month, day] = dateString.split('-').map(Number);
  }
  
  const date = new Date(Date.UTC(year, month - 1, day));
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

/**
 * Format date with weekday (e.g., "Friday, March 15, 2026")
 * @param {string} dateString - Date in YYYY-MM-DD format or ISO timestamp
 * @returns {string} Formatted date string with weekday
 */
export const formatDateLong = (dateString) => {
  if (!dateString) return '';
  
  // Handle both YYYY-MM-DD dates and full ISO timestamps
  let year, month, day;
  
  if (dateString.includes('T')) {
    // It's a timestamp - extract just the date part
    [year, month, day] = dateString.split('T')[0].split('-').map(Number);
  } else {
    // It's already YYYY-MM-DD
    [year, month, day] = dateString.split('-').map(Number);
  }
  
  const date = new Date(Date.UTC(year, month - 1, day));
  
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

/**
 * Format time string (HH:MM:SS or HH:MM) to 12-hour format
 * @param {string} timeString - Time in HH:MM or HH:MM:SS format
 * @returns {string} Formatted time (e.g., "7:30 PM")
 */
export const formatTime = (timeString) => {
  if (!timeString) return '';
  
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours);
  
  return `${hour % 12 || 12}:${minutes} ${hour >= 12 ? 'PM' : 'AM'}`;
};